import ValueList from "slotworks/utils/value-list";

export default class ControlOptions {
    // Options must also be added to the correct model to persist changes.

    public lines: ValueList<number> = new ValueList();

    public creditsPerLine: ValueList<number> = new ValueList();
    public creditSizes: ValueList<number> = new ValueList();

    public autoplays: ValueList<number> = new ValueList();
    public singleWinLimit: ValueList<number> = new ValueList();
    public totalWinLimit: ValueList<number> = new ValueList();
    public lossLimit: ValueList<number> = new ValueList();

    public gambleChoices: ValueList<string> = new ValueList();
    public gambleID: string;

    public stopOnFeatureWin: boolean;
    public stopOnAnyWin: boolean;

    public sessionLimit: ValueList<number> = new ValueList();

    public fixedBetCost: number;
    public selectedLines: number;

    public quickSpin: boolean;
    public turboSpin: boolean;
    public displayCredits: boolean;
    public showIntro: boolean;
    public spaceToSpin: boolean;

    public flashLossLimit: boolean = false;

    public homeURL: string;
    public historyURL: string;
    public onSessionClose: Function;

    public customAutoplayCountSelected: boolean;
    public customLossLimitSelected: boolean;
    public customSingleWinLimitSelected: boolean;

    private totalStakeValues: number[];
    private totalCreditValues: number[];

    public getTotalStakeValues(): number[] {
        if (!this.totalStakeValues) {
            this.totalStakeValues = [];

            for (const creditSize of this.creditSizes.values) {
                for (const stakePerLine of this.creditsPerLine.values) {
                    const stake = Math.round(stakePerLine * creditSize * this.getLineBetMultiplier() * 100) / 100;
                    if (this.totalStakeValues.indexOf(stake) === -1) {
                        this.totalStakeValues.push(stake);
                    }
                }
            }

            // Sort ascending
            this.totalStakeValues.sort((a, b) => a - b);
        }

        return this.totalStakeValues;
    }

    public getTotalCreditValues(): number[] {
        if (!this.totalCreditValues) {
            this.totalCreditValues = [];

            for (const creditsPerLine of this.creditsPerLine.values) {
                const stake = Math.round(creditsPerLine * this.getLineBetMultiplier() * 100) / 100;
                if (this.totalCreditValues.indexOf(stake) === -1) {
                    this.totalCreditValues.push(stake);
                }
            }

            // Sort ascending
            this.totalCreditValues.sort((a, b) => a - b);
        }

        return this.totalCreditValues;
    }

    public setTotalStake(value: number, lockCoinSize: boolean = false) {
        for (const coinSize of this.creditSizes.values) {
            if (lockCoinSize && coinSize !== this.creditSizes.currentValue) {
                continue;
            }
            for (const creditsPerLine of this.creditsPerLine.values) {
                const stake = creditsPerLine * coinSize * this.getLineBetMultiplier();
                // Avoid floating point errors
                if (Math.abs(Math.round(stake * 100 - value * 100)) < 1) {
                    this.creditSizes.currentValue = coinSize;
                    this.creditsPerLine.currentValue = creditsPerLine;
                    return;
                }
            }
        }
    }

    public getMaxStake() {
        const allStakes = this.getTotalStakeValues();
        return allStakes[allStakes.length - 1];
    }

    public getValidLossLimits() {
        return this.getValidLimits(this.lossLimit);
    }

    public getValidSingleWinLimits() {
        return this.getValidLimits(this.singleWinLimit);
    }

    public getValidTotalWinLimits() {
        return this.getValidLimits(this.totalWinLimit);
    }

    public lossLimitAmountIsValid() {
        return this.lossLimit.currentValue > 0 && this.lossLimit.currentValue >= this.getTotalStake();
    }

    public getTotalStake(): number {
        return this.getTotalCredits() * this.creditSizes.currentValue;
    }

    public getTotalCredits(): number {
        return this.creditsPerLine.currentValue * this.getLineBetMultiplier();
    }

    private getLineBetMultiplier() {
        return this.fixedBetCost > 0 ? this.fixedBetCost : this.selectedLines;
    }

    private getValidLimits(limits: ValueList<number>) {
        if (!limits.values || limits.values.length === 0) {
            return null;
        }

        const values = limits.values.concat();

        const hasZero = values[0] === 0;

        while (values[0] < this.getTotalStake()) {
            values.shift();
        }

        if (hasZero) {
            values.unshift(0);
        }

        return values;
    }
}
